/**
 * PUBLIC · one role — /jobs/:slug. Shows the role while its requirement is open and a plain
 * "no longer open" once it is not. "Apply" takes a name and email, then sends the candidate into
 * the existing secure /complete-profile page where they upload their CV (filed unscored unless the
 * team switched the requirement's CV screen on).
 */
import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { PublicShell } from "./PublicJobs.js";
import { MarkdownView } from "../../components/MarkdownView.js";
import { parseJobDescription } from "./jobPosting.js";
import "../../styles/public-jobs.css";

interface OpenJob {
  open: true;
  slug: string;
  title: string;
  companyName: string;
  companySlug: string | null;
  location: string | null;
  workplaceMode: string | null;
  department: string | null;
  experience: string | null;
  skills: string[];
  bonusSkills: string[];
  description: string | null;
  company: { name: string; website: string | null; industry: string | null };
}
interface ClosedJob {
  open: false;
  title: string;
  companyName: string;
  companySlug: string | null;
}

/** Plain text with paragraph breaks — the description is recruiter-authored, never rendered as HTML. */
function plain(text: string): string {
  return text
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function PublicJob(): ReactNode {
  const { slug = "" } = useParams();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [showForm, setShowForm] = useState(false);

  const job = useQuery({
    queryKey: ["public-job", slug],
    queryFn: () => api.get<OpenJob | ClosedJob>(`/public/jobs/${slug}`),
    retry: false,
  });

  // "Apply now" on the jobs list links to /jobs/<slug>#apply — scroll there once the job has loaded
  // (the router doesn't, and the section doesn't exist until the data arrives).
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#apply" && job.isSuccess) document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [hash, job.isSuccess]);

  const apply = useMutation({
    mutationFn: () => api.post<{ ok: boolean; alreadyApplied: boolean; token?: string }>(`/public/jobs/${slug}/apply`, { name, email }),
    onSuccess: (r) => {
      if (r.token) nav(`/complete-profile/${r.token}`);
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    apply.mutate();
  };

  if (job.isLoading) return <PublicShell><p className="pj-muted">Loading…</p></PublicShell>;
  if (job.isError || !job.data) {
    return (
      <PublicShell>
        <h1 className="pj-h1">This page isn’t available</h1>
        <p className="pj-muted">The link may be wrong, or the role was withdrawn.</p>
        <Link to="/jobs" className="pj-link">See open roles</Link>
      </PublicShell>
    );
  }
  const j = job.data;
  if (!j.open) {
    return (
      <PublicShell>
        <h1 className="pj-h1">{j.title}</h1>
        <p className="pj-closed">This role at {j.companyName} is no longer open.</p>
        <p>
          <Link to={j.companySlug ? `/careers/${j.companySlug}` : "/jobs"} className="pj-link">
            See other open roles{j.companySlug ? ` at ${j.companyName}` : ""}
          </Link>
        </p>
      </PublicShell>
    );
  }

  const meta = [j.location, j.workplaceMode, j.department, j.experience].filter((m): m is string => Boolean(m));
  const alreadyApplied = apply.data?.alreadyApplied;
  const posting = j.description ? parseJobDescription(plain(j.description), j.title) : null;
  const initial = j.companyName.trim().charAt(0).toUpperCase() || "?";

  // The apply card — in the header's right column (25%), so it is the first thing you can act on.
  const applyCard = (
    <section className="pj-apply" aria-label="Apply" id="apply">
      {alreadyApplied ? (
        <p className="pj-apply-done">
          We already have an application from <strong>{email}</strong> for this role. If you didn’t finish
          uploading your CV, check the email we sent you, or contact {j.companyName}.
        </p>
      ) : showForm ? (
        <form onSubmit={submit}>
          <label>
            Full name
            <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={160} autoComplete="name" />
          </label>
          <label>
            Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={320} autoComplete="email" />
          </label>
          <button className="btn primary pj-apply-btn" disabled={apply.isPending}>
            {apply.isPending ? "One moment…" : "Continue — add my CV"}
          </button>
          {apply.isError && (
            <p className="pj-warn" role="alert">
              {apply.error instanceof ApiError ? apply.error.message : "Could not start your application."}
            </p>
          )}
          <p className="pj-apply-note">Next you’ll upload your CV and confirm a few details. A person reviews every application.</p>
        </form>
      ) : (
        <>
          <button className="btn primary pj-apply-btn" onClick={() => setShowForm(true)}>
            Apply now
          </button>
          <p className="pj-apply-note">Takes a minute: your name, email and CV. A person reviews every application.</p>
        </>
      )}
    </section>
  );

  return (
    <PublicShell wide>
      {/* Dark header: company, title, key facts (75%) · Apply now (25%) */}
      <header className="pj-hero">
        <div className="pj-hero-main">
          <div className="pj-hero-co">
            <span className="pj-hero-logo" aria-hidden="true">{initial}</span>
            {j.companySlug ? <Link to={`/careers/${j.companySlug}`}>{j.companyName}</Link> : <span>{j.companyName}</span>}
          </div>
          <h1 className="pj-hero-title">{j.title}</h1>
          {meta.length > 0 && (
            <ul className="pj-hero-facts">
              {meta.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
          {j.skills.length > 0 && (
            <ul className="pj-hero-skills" aria-label="Skills">
              {j.skills.slice(0, 8).map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="pj-hero-side">{applyCard}</div>
      </header>

      <div className="pj-body">
        <article className="pj-main">
          {posting?.body && (
            <section className="pj-section">
              <h2 className="pj-h2">About the role</h2>
              <MarkdownView content={posting.body} className="pj-md" />
            </section>
          )}
          {j.skills.length > 0 && (
            <section className="pj-section">
              <h2 className="pj-h2">Skills</h2>
              <ul className="pj-chips">
                {j.skills.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </section>
          )}
        </article>

        {posting && posting.details.length > 0 && (
          <aside className="pj-details" aria-label="Job details">
            <h2 className="pj-h2">Job details</h2>
            <dl>
              {posting.details.map((d) => (
                <div key={d.label}>
                  <dt>{d.label}</dt>
                  <dd>{d.value}</dd>
                </div>
              ))}
            </dl>
            <a href="#apply" className="btn primary pj-apply-btn">Apply now</a>
          </aside>
        )}
      </div>
    </PublicShell>
  );
}
